import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { PHONE_TEL, PHONE_DISPLAY } from "../content/siteContent";
import { IconChevronDown, IconClose, IconNav, IconPhone, IconWhatsApp } from "./Icons";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeToggle from "./ThemeToggle";

const MobileNavigation = ({ open, onClose }) => {
    const { t, isRtl, lang } = useLanguage();
    const reduce = useReducedMotion();
    const closeRef = useRef(null);
    const prevLang = useRef(lang);
    const [openGroup, setOpenGroup] = useState("services");

    useEffect(() => {
        if (prevLang.current !== lang) {
            prevLang.current = lang;
            if (open) onClose();
        }
    }, [lang, open, onClose]);

    useEffect(() => {
        if (!open) return;
        closeRef.current?.focus();
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    const hidden = isRtl ? { x: "100%" } : { x: "-100%" };
    const toggleGroup = (id) => {
        setOpenGroup((current) => (current === id ? null : id));
    };

    return createPortal(
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label={t.header.menuTitle} data-testid="mobile-nav-drawer">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: reduce ? 0 : 0.25 }}
                        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={reduce ? { opacity: 0 } : hidden}
                        animate={reduce ? { opacity: 1 } : { x: 0 }}
                        exit={reduce ? { opacity: 0 } : hidden}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-y-0 start-0 flex w-[86%] max-w-sm flex-col bg-sand shadow-2xl"
                    >
                        <div className="flex items-center justify-between border-b border-ink/5 px-6 py-5">
                            <span className="flex items-center gap-3">
                                <img src="/assets/thera-symbol-original.png" alt="" className="h-9 w-9 object-contain" />
                                <span className="font-display text-lg text-pine">{t.brand}</span>
                            </span>
                            <button
                                ref={closeRef}
                                type="button"
                                onClick={onClose}
                                aria-label={t.header.closeMenu}
                                data-testid="mobile-nav-close"
                                className="flex min-h-11 min-w-11 items-center justify-center rounded-full text-pine transition-colors duration-300 hover:bg-gold-100"
                            >
                                <IconClose />
                            </button>
                        </div>
                        <nav aria-label={t.header.menuTitle} className="flex-1 px-6 py-4">
                            <motion.ul
                                className="space-y-1"
                                initial={reduce ? false : "hidden"}
                                animate="show"
                                variants={{ show: { transition: { staggerChildren: 0.05, delayChildren: 0.12 } } }}
                            >
                                {t.nav.map((item) => (
                                    <motion.li
                                        key={item.id}
                                        variants={{
                                            hidden: { opacity: 0, x: isRtl ? 28 : -28 },
                                            show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
                                        }}
                                    >
                                        <div className="border-b border-ink/5 py-3">
                                            <button
                                                type="button"
                                                onClick={() => toggleGroup(item.id)}
                                                aria-expanded={openGroup === item.id}
                                                aria-controls={`mobile-nav-group-${item.id}`}
                                                data-testid={`mobile-nav-group-${item.id}`}
                                                className="flex min-h-11 w-full items-center gap-3 text-start text-base font-semibold text-pine transition-colors duration-300 hover:text-gold-600"
                                            >
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-100 text-gold-600">
                                                    <IconNav name={item.icon} className="h-4 w-4" />
                                                </span>
                                                <span className="flex-1">{item.label}</span>
                                                <IconChevronDown className={`h-4 w-4 transition-transform duration-300 ${openGroup === item.id ? "rotate-180" : ""}`} />
                                            </button>
                                            {openGroup === item.id && (
                                                <div id={`mobile-nav-group-${item.id}`} className="mt-1 space-y-1 ps-11">
                                                    {(item.children || [item]).map((link) => (
                                                        <a
                                                            key={link.id}
                                                            href={`#${link.id}`}
                                                            onClick={onClose}
                                                            data-testid={`mobile-nav-link-${link.id}`}
                                                            className="flex min-h-10 items-center gap-2 py-1 font-display text-lg text-ink whitespace-nowrap transition-colors duration-300 hover:text-gold-600 sm:text-xl"
                                                        >
                                                            <IconNav name={link.icon} className="h-4 w-4 shrink-0 text-gold-600" />
                                                            {link.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </motion.li>
                                ))}
                            </motion.ul>
                        </nav>
                        <div className="space-y-2 border-t border-ink/5 px-6 py-4">
                            <a
                                href="#appointment"
                                onClick={onClose}
                                data-testid="mobile-nav-whatsapp"
                                className="flex min-h-11 items-center justify-center gap-2 rounded-full bg-gold-500 px-5 text-sm font-semibold text-ink whitespace-nowrap transition-colors duration-300 hover:bg-gold-600"
                            >
                                <IconWhatsApp className="h-5 w-5" />
                                {t.hero.ctaPrimary}
                            </a>
                            <a
                                href={`tel:${PHONE_TEL}`}
                                data-testid="mobile-nav-call"
                                className="flex min-h-11 items-center justify-center gap-2 rounded-full border border-pine/20 px-5 text-sm font-semibold text-pine whitespace-nowrap transition-colors duration-300 hover:border-gold-500"
                            >
                                <IconPhone className="h-4 w-4" />
                                <span dir="ltr">{PHONE_DISPLAY}</span>
                            </a>
                            <div className="flex items-center gap-2 pt-1">
                                <LanguageSwitcher className="flex-1 justify-center" />
                                <ThemeToggle />
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
};

export default MobileNavigation;
